// ============================================================
// hooks/useFirestore.js — Custom Hooks for Firestore Data
// ============================================================
// Real-time listeners + one-shot fetchers for all collections

import { useState, useEffect, useCallback } from "react";
import {
  ProductService,
  BookingService,
  ReviewService,
  NotificationService,
  LoyaltyService,
  PromotionService,
  BusinessSettingsService,
  UserService,
  CampaignService,
} from "../services/firebaseServices";

// ============================================================
// PRODUCTS — Real-time listener
// ============================================================
export function useProducts(adminMode = false) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (adminMode) {
      // Admin sees all products (including unavailable)
      ProductService.getAllAdmin()
        .then(setProducts)
        .catch(setError)
        .finally(() => setLoading(false));
    } else {
      // Customer: real-time listener on available products
      const unsub = ProductService.onProductsChange((data) => {
        setProducts(data);
        setLoading(false);
      });
      return () => unsub();
    }
  }, [adminMode]);

  const refresh = useCallback(async () => {
    setLoading(true);
    const data = adminMode
      ? await ProductService.getAllAdmin()
      : await ProductService.getAll();
    setProducts(data);
    setLoading(false);
  }, [adminMode]);

  return { products, loading, error, refresh };
}

// ============================================================
// BOOKINGS — Real-time for admin, one-shot for customer
// ============================================================
export function useBookings(isAdmin = false) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdmin) {
      const unsub = BookingService.onBookingsChange((data) => {
        setBookings(data);
        setLoading(false);
      });
      return () => unsub();
    } else {
      BookingService.getMyBookings()
        .then(setBookings)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [isAdmin]);

  const refresh = useCallback(async () => {
    setLoading(true);
    const data = isAdmin
      ? await BookingService.getAllAdmin()
      : await BookingService.getMyBookings();
    setBookings(data);
    setLoading(false);
  }, [isAdmin]);

  return { bookings, loading, refresh };
}

// ============================================================
// REVIEWS
// ============================================================
export function useReviews(productId = null) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const data = productId
          ? await ReviewService.getByProduct(productId)
          : await ReviewService.getAllAdmin();
        setReviews(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, [productId]);

  return { reviews, loading };
}

// ============================================================
// NOTIFICATIONS — Real-time
// ============================================================
export function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const unsub = NotificationService.onNotificationsChange((data) => {
      setNotifications(data);
      setUnreadCount(data.filter((n) => !n.isRead).length);
    });
    return () => unsub();
  }, []);

  const markAsRead = useCallback(async (id) => {
    await NotificationService.markAsRead(id);
  }, []);

  const markAllAsRead = useCallback(async () => {
    await NotificationService.markAllAsRead();
  }, []);

  return { notifications, unreadCount, markAsRead, markAllAsRead };
}

// ============================================================
// LOYALTY
// ============================================================
export function useLoyalty() {
  const [config, setConfig] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [cfg, txns] = await Promise.all([
          LoyaltyService.getConfig(),
          LoyaltyService.getMyTransactions(),
        ]);
        setConfig(cfg);
        setTransactions(txns);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  return { config, transactions, loading };
}

// ============================================================
// PROMOTIONS
// ============================================================
export function usePromotions(adminMode = false) {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = adminMode
          ? await PromotionService.getAllAdmin()
          : await PromotionService.getActive();
        setPromotions(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [adminMode]);

  return { promotions, loading };
}

// ============================================================
// BUSINESS SETTINGS
// ============================================================
export function useBusinessSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    BusinessSettingsService.get()
      .then(setSettings)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const refresh = useCallback(async () => {
    const data = await BusinessSettingsService.get();
    setSettings(data);
  }, []);

  return { settings, loading, refresh };
}

// ============================================================
// CUSTOMERS (admin)
// ============================================================
export function useCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    UserService.getAllCustomers()
      .then(setCustomers)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    const data = await UserService.getAllCustomers();
    setCustomers(data);
    setLoading(false);
  }, []);

  return { customers, loading, refresh };
}

// ============================================================
// CAMPAIGNS (admin)
// ============================================================
export function useCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    CampaignService.getAll()
      .then(setCampaigns)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    const data = await CampaignService.getAll();
    setCampaigns(data);
    setLoading(false);
  }, []);

  return { campaigns, loading, refresh };
}